/**
 * Video file utilities (e.g. duration from File for upload/limit checks).
 * Reusable across components that need to read video metadata in the browser.
 */

/** Max time to wait for video metadata to load (ms). */
export const DURATION_LOAD_TIMEOUT_MS = 10000;

/**
 * Get video duration in seconds from a File using HTML5 video.
 * @param file - Video file
 * @returns Promise resolving to duration in seconds
 * @throws Error if duration cannot be read or times out
 */
export function getVideoDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Could not read video duration (timeout)."));
    }, DURATION_LOAD_TIMEOUT_MS);

    function cleanup() {
      clearTimeout(timeoutId);
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("error", onError);
      URL.revokeObjectURL(url);
    }

    function onLoaded() {
      const duration = video.duration;
      cleanup();
      if (Number.isFinite(duration) && duration > 0) {
        resolve(duration);
      } else {
        reject(new Error("Invalid video duration."));
      }
    }

    function onError() {
      cleanup();
      reject(new Error("Could not read video duration."));
    }

    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("error", onError);
    video.src = url;
  });
}
